import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { StateValues } from 'react-use-form-state';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import * as Styled from './styled';
import { FileToUpload } from 'src/ui/atoms/file-upload';
import Spacer from 'src/ui/atoms/spacer';
import Hint from 'src/ui/atoms/hint';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import Section from 'src/ui/atoms/section';
import { Header } from 'src/ui/atoms/header';
import Text from 'src/ui/polymorphs/text';
import { ButtonSizes } from 'src/ui/styles/themes/buttons';
import { ActionButton } from 'src/ui/molecules/action-buttons';
import { useAddDocuments } from './hooks';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';

export interface AddDocumentsModalProps {
  onAddNewDocument: () => void;
  onClose: () => void;
}

export interface AddDocumentsFields {
  documents: FileToUpload[];
  productId: string;
}

export enum ModalState {
  DEFAULT = 'default',
  VALID = 'valid',
  PARTIAL_VALID = 'partial_valid',
  INVALID = 'invalid',
}

export const Form = makeModalForm<AddDocumentsFields>();

const AddDocumentsModal: FunctionComponent<AddDocumentsModalProps> = ({ onAddNewDocument, onClose }) => {
  const {
    modalState,
    successfulUploadedDocumentIds,
    noOfDocumentsToUpload,
    products,
    debouncedProductSearch,
    apiError,
    fileErrors,
    loading,
    getDocumentTypes,
    setSelectedProduct,
    onDiscardAllDocuments,
    onAddValidDocuments,
    onChangeBatchDocuments,
    onSubmit,
  } = useAddDocuments({
    onAddNewDocument,
    onClose,
  });

  const translate = useTranslateWithStringArgs();

  const getModalHeader = useCallback(() => {
    if (modalState === ModalState.DEFAULT || modalState === ModalState.PARTIAL_VALID) {
      return `${translate('addDocuments.title.default')}`;
    } else if (modalState === ModalState.VALID) {
      return `${translate('addDocuments.title.valid')}`;
    } else if (modalState === ModalState.INVALID) {
      return `${translate('addDocuments.title.invalid')}`;
    }
  }, [modalState]);

  const renderSuccessMessage = useCallback(() => {
    return (
      <Hint variant="success">
        <Translate name="addDocuments.successNotification" args={[successfulUploadedDocumentIds.length]} />
      </Hint>
    );
  }, [successfulUploadedDocumentIds.length]);

  const renderNegativeMessage = useCallback(() => {
    const failedCount = noOfDocumentsToUpload - successfulUploadedDocumentIds.length;
    return (
      <Hint variant="danger">
        <Translate name="addDocuments.negativeNotification" args={[failedCount, noOfDocumentsToUpload]} />
      </Hint>
    );
  }, [successfulUploadedDocumentIds.length, noOfDocumentsToUpload]);

  const renderPartialValidMessage = useCallback(() => {
    return (
      <Hint variant="success">
        <Translate
          name="addDocuments.partialValidNotification"
          args={[successfulUploadedDocumentIds.length, noOfDocumentsToUpload]}
        />
      </Hint>
    );
  }, [successfulUploadedDocumentIds.length, noOfDocumentsToUpload]);

  const renderInvalidReason = useCallback(() => {
    return (
      <>
        {Object.entries(fileErrors).map(([fileName, errorCodes]) => (
          <Hint key={fileName} variant="info">
            <div>
              <Styled.ErrorTitle>{fileName}</Styled.ErrorTitle>
              <Styled.ErrorContainer>
                {!errorCodes && <li key={fileName}>{translate(`addDocuments.errors.noNetwork`)}</li>}
                {errorCodes &&
                  errorCodes.map((code, index) => (
                    <li key={index}>{translate(`addDocuments.errors.${snakeToCamelCaseWithNumbers(code)}`)}</li>
                  ))}
              </Styled.ErrorContainer>
            </div>
          </Hint>
        ))}
      </>
    );
  }, [fileErrors]);

  const Buttons = {
    Cancel: {
      name: 'cancel',
      content: <Translate name="common.cancel" />,
      size: 'large' as ButtonSizes,
      disabled: loading,
      onClick: () => onClose(),
    },
    Close: {
      name: 'close',
      content: <Translate name="common.close" />,
      size: 'large' as ButtonSizes,
      onClick: () => onAddNewDocument(),
    },
    Submit: {
      name: 'send',
      content: <Translate name="common.add" />,
      variant: 'primary',
      loading: loading,
      disabled: loading,
      type: 'submit',
      size: 'large' as ButtonSizes,
    },
    AddValidDocuments: {
      name: 'addValid',
      content: <Translate name="addDocuments.addValidDocuments" />,
      variant: 'primary',
      disabled: loading,
      type: 'submit',
      size: 'large' as ButtonSizes,
      onClick: () => onAddValidDocuments(),
    },
    DiscardAllDocuments: {
      name: 'discard',
      content: <Translate name="addDocuments.discardAllDocuments" />,
      loading: loading,
      size: 'large' as ButtonSizes,
      onClick: () => onDiscardAllDocuments(),
    },
  };

  const actionButtons = useMemo(() => {
    if (modalState === ModalState.DEFAULT) {
      return [Buttons.Cancel, Buttons.Submit] as ActionButton[];
    } else if (modalState === ModalState.PARTIAL_VALID) {
      return [Buttons.DiscardAllDocuments, Buttons.AddValidDocuments] as ActionButton[];
    } else if (modalState === ModalState.VALID || modalState === ModalState.INVALID) {
      return [Buttons.Close] as ActionButton[];
    }
  }, [modalState, loading]);

  return (
    <Modal onClose={onClose} disableClose={loading}>
      <Form
        i18nKey="addDocuments"
        onSubmit={(values: StateValues<AddDocumentsFields>) => {
          onSubmit(values);
        }}
        error={apiError}
      >
        <ModalHeader>{getModalHeader()}</ModalHeader>
        <ModalContent>
          {modalState === ModalState.VALID && renderSuccessMessage()}
          {modalState === ModalState.INVALID && (
            <>
              {renderNegativeMessage()}
              {renderInvalidReason()}
            </>
          )}
          {modalState === ModalState.PARTIAL_VALID && (
            <>
              {renderPartialValidMessage()}
              {renderNegativeMessage()}
              {renderInvalidReason()}
            </>
          )}
          {modalState === ModalState.DEFAULT && (
            <>
              <Section spacing="regular">
                <Translate name="addDocuments.description1" as="p" />
                <Text fontSize={14}>
                  <Translate name="addDocuments.fileFormat" as="b" />
                </Text>
                <Spacer y={1} />
                <p>
                  <Translate name="addDocuments.fileFormatName" as="div" />
                </p>
                <Translate name="addDocuments.description2" />
              </Section>
              <Section spacing="regular">
                <Form.Group name="documents" required={true}>
                  <Form.MultipleFilesUpload
                    onChange={(documents: FileToUpload[]) => {
                      onChangeBatchDocuments(documents);
                    }}
                    documentTypes={getDocumentTypes()}
                    groupFiles={true}
                    isDocumentTypeRequired={false}
                  />
                </Form.Group>
              </Section>

              <Header spacing="medium">
                <Translate name="addDocuments.linkDocuments" />
              </Header>
              <Form.Group name="productId" required={true}>
                <Form.Select
                  options={products.map((product) => ({
                    value: product.id,
                    label: product.name,
                  }))}
                  onInputChange={debouncedProductSearch}
                  onChange={(productId: string) => {
                    const foundProduct = products.find((product) => product.id === productId);
                    setSelectedProduct(foundProduct);
                  }}
                ></Form.Select>
              </Form.Group>
            </>
          )}
          <Form.GenericErrorMessages />
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons actionButtons={actionButtons} />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddDocumentsModal;
